import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardSlide from './slides/DashboardSlide';
import BookingSlide from './slides/BookingSlide';
import CoachingSlide from './slides/CoachingSlide';
import InvoiceSlide from './slides/InvoiceSlide';
import MethodsSlide from './slides/MethodsSlide';
import QualitySlide from './slides/QualitySlide';
import StripeSlide from './slides/StripeSlide';
import FinalSlide from './slides/FinalSlide';
import './DemoTour.css';

const TOTAL_SLIDES = 7;
const AUTO_PLAY_INTERVAL = 8000; // 8 seconds per slide
const slideComponents = [DashboardSlide, BookingSlide, CoachingSlide, InvoiceSlide, MethodsSlide, QualitySlide, StripeSlide];

interface DemoTourProps {
  onClose: () => void;
}

const DemoTour: React.FC<DemoTourProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const [paused, setPaused] = useState(false);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isFinal = currentSlide === TOTAL_SLIDES;
  const steps = t('demoTour.steps', { returnObjects: true }) as string[];

  const goTo = useCallback((index: number) => {
    if (animating) return;
    setAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setAnimating(false), 400);
  }, [animating]);

  const next = useCallback(() => {
    if (currentSlide < TOTAL_SLIDES) goTo(currentSlide + 1);
  }, [currentSlide, goTo]);

  const prev = useCallback(() => {
    if (currentSlide > 0) goTo(currentSlide - 1);
  }, [currentSlide, goTo]);

  // Auto-play: advance slide every 8s, pause on hover or manual interaction
  useEffect(() => {
    if (autoPlay && !paused && !isFinal && !animating) {
      autoPlayRef.current = setInterval(() => {
        setCurrentSlide((s) => {
          if (s < TOTAL_SLIDES) {
            setAnimating(true);
            setTimeout(() => setAnimating(false), 400);
            return s + 1;
          }
          return s;
        });
      }, AUTO_PLAY_INTERVAL);
    }
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [autoPlay, paused, isFinal, animating, currentSlide]);

  const handleManualNav = useCallback((action: () => void) => {
    setAutoPlay(false); // stop auto-play on manual interaction
    action();
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); handleManualNav(next); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); handleManualNav(prev); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, next, prev, handleManualNav]);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const slides = t('demoTour.slides', { returnObjects: true }) as {
    stepLabel: string; title: string; description: string; highlights: string[];
  }[];

  return (
    <div className="dt-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="dt-container"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Top bar */}
        <div className="dt-topbar">
          <span className="dt-topbar-title">{t('demoTour.topbarTitle')}</span>
          <button className="dt-close" onClick={onClose} aria-label={t('demoTour.close')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress bar */}
        {!isFinal && (
          <div className="dt-progress">
            {steps.map((label, i) => (
              <button
                key={label}
                className={`dt-progress-step ${i < currentSlide ? 'dt-progress-step--done' : ''} ${i === currentSlide ? 'dt-progress-step--active' : ''}`}
                onClick={() => handleManualNav(() => goTo(i))}
              >
                <div
                  className="dt-progress-bar"
                  style={
                    i === currentSlide && autoPlay && !paused
                      ? { animation: `dt-fill ${AUTO_PLAY_INTERVAL}ms linear forwards` }
                      : i === currentSlide && autoPlay && paused
                      ? { animationPlayState: 'paused' }
                      : undefined
                  }
                />
                <span className="dt-progress-label">{label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Slide content */}
        <div className="dt-body">
          {isFinal ? (
            <div className="dt-slide dt-slide--final" key="final">
              <FinalSlide />
            </div>
          ) : (
            <div className="dt-slide" key={currentSlide}>
              {/* Left: Text */}
              <div className="dt-slide-text">
                <div className="dt-slide-step">{slides[currentSlide].stepLabel}</div>
                <h2 className="dt-slide-title">{slides[currentSlide].title}</h2>
                <p className="dt-slide-description">{slides[currentSlide].description}</p>
                <ul className="dt-slide-highlights">
                  {slides[currentSlide].highlights.map((h) => (
                    <li key={h}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
              {/* Right: Mockup */}
              <div className="dt-slide-visual">
                <div className="dt-mockup-wrapper">
                  {React.createElement(slideComponents[currentSlide])}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bottom nav */}
        <div className="dt-nav">
          {currentSlide > 0 && !isFinal ? (
            <button className="dt-nav-prev" onClick={() => handleManualNav(prev)}>{t('demoTour.prev')}</button>
          ) : <span />}
          {!isFinal && (
            <span className="dt-nav-counter">
              {t('demoTour.counter', { current: currentSlide + 1, total: TOTAL_SLIDES })}
            </span>
          )}
          {isFinal ? (
            <button className="dt-nav-close" onClick={onClose}>{t('demoTour.close')}</button>
          ) : currentSlide === TOTAL_SLIDES - 1 ? (
            <button className="dt-nav-next dt-nav-next--finish" onClick={() => handleManualNav(next)}>{t('demoTour.finish')}</button>
          ) : (
            <button className="dt-nav-next" onClick={() => handleManualNav(next)}>{t('demoTour.next')}</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoTour;
